/**
 * integratedScope.js — 작업 대상 "통합관리" 판단 (2026-10-01, 사용자 확정 A안: 회사 칸 맨 아래)
 *
 * 가짜 회사 코드를 만들지 않는다. **통합 모드 = 지금 주소가 통합 주소 + 이 사람이 통합관리를 쓸 수 있음.**
 *   가짜 코드를 고르면 저장값(companyScope.company)·서버 회사값(X-Scope-Company)·회사별 주소(/admin/ext/…)로
 *   그대로 흘러간다. 주소로 정하면 그럴 일이 없고, 새로고침·뒤로가기·위젯 링크로 들어와도 선택창과 사이드바가 맞는다.
 *
 * 통합 메뉴 표(integrated_menus)에는 테넌트 칸이 없다 — 그래서 운영자는 "메뉴의 회사가 모두 지금 고른 테넌트
 *   회사 안에 있는 메뉴"만 본다. 다른 사람은 서버가 열쇠(INTEGRATED_MENU_{id})로 이미 거른 목록 그대로다.
 */
import { COMPANY_MENU_FEATURES } from './companyFeatures';
import { categoryOfPath, orderGroupNames } from './menuCategories';
import { HOME_PATH } from './companySwitchTarget';
import { INTEGRATED_FEATURES } from '../integratedmenu/integratedFeatureMeta';

export const INTEGRATED_HOME_PATH = '/admin/integrated';
export const INTEGRATED_MANAGE_PATH = '/admin/integrated-menus';
/** 회사 칸의 "통합관리" 칸 값 — 회사 코드로 쓰지 않는다(selectCompany·저장소로 넘기지 않음). */
export const INTEGRATED_OPTION = '__integrated__';
/** 따라갈 일반 메뉴가 없는 종류가 놓이는 묶음. 위치만 정하고 보이는지와는 무관하다. */
export const OTHER_GROUP = '기타';

const upper = (c) => String(c || '').trim().toUpperCase();

/** 통합 주소인가 — 홈 / 통합 메뉴 화면 / 통합 메뉴 관리. */
export function isIntegratedPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '');
  return p === INTEGRATED_HOME_PATH || p.startsWith(`${INTEGRATED_HOME_PATH}/`) || p === INTEGRATED_MANAGE_PATH;
}

/**
 * 이 사람에게 통합관리에서 보일 통합 메뉴.
 * @param {object} p
 * @param {boolean} p.isOperator
 * @param {Array} p.list                   /api/my/integrated-menus 목록(운영자는 전 테넌트 메뉴)
 * @param {string[]|null} p.tenantCompanyCodes 운영자: 지금 고른 테넌트의 회사 코드. 아직 그 테넌트 것이 아니면 null.
 */
export function scopedIntegratedMenus({ isOperator, list, tenantCompanyCodes }) {
  if (!Array.isArray(list)) return [];
  // 다른 사람: 더 거르면 열쇠를 가진 사람이 오히려 메뉴를 잃는다.
  if (!isOperator) return list;
  // 운영자: 회사 목록을 모르면 아무것도 안 보인다(fail-closed) — 다른 테넌트 메뉴가 섞이지 않게.
  if (!Array.isArray(tenantCompanyCodes) || tenantCompanyCodes.length === 0) return [];
  const allowed = new Set(tenantCompanyCodes.map(upper).filter(Boolean));
  return list.filter((m) => {
    const codes = Array.isArray(m?.company_codes) ? m.company_codes.map(upper).filter(Boolean) : [];
    return codes.length > 0 && codes.every((c) => allowed.has(c));
  });
}

/** 사이드바·홈에 그릴 이름 — 앞의 "*"만 뺀다(DB 이름·권한 이름·배정 화면은 그대로). */
export function displayMenuName(name) {
  return String(name || '').replace(/^\s*\*+\s*/, '');
}

/** 회사 표시 순서 — 본사(이 사이트의 주인 회사) 먼저, 나머지는 가나다(시안 "PINE·DOT"). DB 는 가나다로 저장한다. */
export function orderCompanyCodes(codes, hqCode) {
  const hq = upper(hqCode);
  return (Array.isArray(codes) ? codes : []).map((c) => String(c || '').trim()).filter(Boolean)
    .sort((a, b) => (upper(a) === hq ? -1 : 0) - (upper(b) === hq ? -1 : 0) || a.localeCompare(b));
}

/**
 * 회사 코드 → 작업 대상과 같은 회사 이름(2026-10-15 사용자: "작업대상과 같이 유기자차 무기자차로 떠야지").
 *   여러 목록을 받으면 앞 목록 이름이 먼저. 목록에 없으면 코드 그대로.
 * @returns {(code: string) => string}
 */
export function companyNameLookup(...lists) {
  const map = new Map();
  for (const list of lists) {
    for (const c of Array.isArray(list) ? list : []) {
      const k = upper(c?.code);
      if (k && c?.name && !map.has(k)) map.set(k, String(c.name));
    }
  }
  return (code) => map.get(upper(code)) || String(code || '');
}

const firstChar = (s) => ([...String(s || '')][0] || '').toUpperCase();

/**
 * 회사 표시 글자. PC 는 이름 첫 글자(유·무 / 파·D·L — 2026-10-15 사용자 선택), 첫 글자가 겹치면 이름 전체.
 *   nameOf 를 안 주면 예전처럼 코드 기준(P·D·L, 겹치면 SUNCREAM·SUNCREAM2).
 * @returns {{code: string, name: string, short: string}[]}
 */
export function companyBadges(codes, hqCode = null, nameOf = null) {
  const list = orderCompanyCodes(codes, hqCode);
  const label = (c) => (nameOf ? nameOf(c) : c) || c;
  const firsts = list.map((c) => firstChar(label(c)));
  const clash = new Set(firsts).size !== firsts.length;
  return list.map((code) => ({ code, name: label(code), short: clash ? label(code) : firstChar(label(code)) }));
}

/** 휴대폰 회사 표시 글자 — 이름 전체 "유기자차·무기자차"(nameOf 없으면 코드 "PINE·DOT"). */
export function companyBadgeText(codes, hqCode = null, nameOf = null) {
  return orderCompanyCodes(codes, hqCode).map((c) => (nameOf ? nameOf(c) : c) || c).join('·');
}

/** 기능 종류 → 같은 일을 하는 일반 메뉴 주소(본사). 모르면 null. */
export function integratedHqPath(featureType) {
  const feature = INTEGRATED_FEATURES.find((f) => f.value === featureType);
  if (feature?.hqPath) return feature.hqPath; // 회사별 메뉴가 없는 기능(방문빈도 관리, 2026-10-29)
  const key = feature?.featureKey;
  return COMPANY_MENU_FEATURES.find((f) => f.key === key)?.hqPath || null;
}

/** 통합 메뉴가 놓일 묶음과 아이콘 — 같은 기능의 일반 메뉴가 놓인 분류(DB 분류를 따른다)를 그대로 따라간다. */
export function integratedMenuPlace(menu, menuGroups) {
  const hqPath = integratedHqPath(menu?.feature_type);
  const groups = Array.isArray(menuGroups) ? menuGroups : [];
  const item = hqPath ? groups.flatMap((g) => g.items || []).find((it) => it.to === hqPath) : null;
  return { group: categoryOfPath(groups, hqPath) || OTHER_GROUP, Icon: item?.Icon || null };
}

const compareMenus = (a, b) => (Number(a.menu.sort_order) || 0) - (Number(b.menu.sort_order) || 0)
  || a.label.localeCompare(b.label, 'ko')
  || Number(a.menu.id) - Number(b.menu.id);

/**
 * 통합 사이드바 묶음. 묶음 순서 = 일반 사이드바와 같은 규칙(orderGroupNames), 묶음 안 = 정렬값 → 가나다.
 * @returns {{group: string, items: {menu, to, label, Icon}[]}[]}
 */
export function groupIntegratedMenus(menus, menuGroups, dbOrder = []) {
  const byGroup = new Map();
  for (const menu of Array.isArray(menus) ? menus : []) {
    const { group, Icon } = integratedMenuPlace(menu, menuGroups);
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push({ menu, to: `${INTEGRATED_HOME_PATH}/${menu.id}`, label: displayMenuName(menu.name), Icon });
  }
  const codeOrder = (Array.isArray(menuGroups) ? menuGroups : []).map((g) => g.group);
  return orderGroupNames([...byGroup.keys()], dbOrder || [], codeOrder)
    .map((group) => ({ group, items: byGroup.get(group).sort(compareMenus) }));
}

/** 사이드바 맨 위 고정 줄 '상황판'(SITUATION_BOARD) — 홈 열쇠가 없을 때 홈 대신 갈 자리. */
export const BOARD_PATH = '/admin/board';

/**
 * 통합 모드에서 회사를 골랐을 때 갈 곳. 회사 전환 규칙(resolveCompanySwitchTarget)의 결과를 받아,
 *   그대로(null)이거나 또 통합 주소면 빠져나올 수 없으니 홈으로 바꾼다.
 *   홈 = 운영자·HOME 열쇠 보유자는 /admin/home. HOME 열쇠가 없는 사람을 /admin/home 으로 보내면 직원 관리 화면으로
 *   다시 튕겨 사이드바와 화면이 어긋난다. 그래서 홈 열쇠가 없으면(2026-10-29 사용자 결정 나):
 *   ① 통합관리에 들어오기 전에 보던 회사 화면(backTo — 고른 회사에 맞춘 것) → ② 상황판(열쇠가 있으면) → ③ '/'.
 *   예전엔 바로 '/' 였는데, '/'가 마지막으로 본 화면(방금 보던 통합 메뉴)을 되살려 통합관리로 되돌아갔다
 *   (파인그룹 김예현·표정 — 홈 열쇠 없음). ③으로 갈 때는 부르는 쪽이 통합 메뉴 되살리기를 한 번 막는다(lastScreen).
 */
export function exitIntegratedTarget(target, { canSeeHome, canSeeBoard = false, backTo = null }) {
  const to = target && !isIntegratedPath(target) ? target : HOME_PATH;
  if (to !== HOME_PATH || canSeeHome) return to;
  if (backTo && !isIntegratedPath(backTo) && backTo !== HOME_PATH) return backTo;
  return canSeeBoard ? BOARD_PATH : '/';
}
